import { useRef, useState, useEffect } from 'react';

export function Cities() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScrollPosition();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      return () => container.removeEventListener('scroll', checkScrollPosition);
    }
  }, []);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const openChatbot = () => {
    const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (btn) btn.click();
  };

  const cities = [
    {
      name: 'Vancouver',
      note: 'Strict owner-occupant requirements',
      image: 'https://public.readdy.ai/ai/img_res/740cbe6655aabab5de97467f0775a5a9.jpg',
      link: '/vancouver-short-term-rental-rules'
    },
    {
      name: 'North Vancouver',
      note: 'Zoning-specific regulations',
      image: 'https://readdy.ai/api/search-image?query=North%20Vancouver%20residential%20neighborhood%20with%20mountain%20backdrop%2C%20modern%20homes%20and%20tree-lined%20streets%2C%20peaceful%20suburban%20setting%20with%20natural%20mountain%20scenery%2C%20professional%20real%20estate%20photography%20style&width=280&height=360&seq=city-north-van-001&orientation=portrait',
      link: '/north-vancouver-str-bylaws'
    },
    {
      name: 'Burnaby',
      note: 'Principal residence focus',
      image: 'https://readdy.ai/api/search-image?query=Burnaby%20BC%20Metrotown%20skyline%20with%20modern%20high%20rise%20condos%20and%20urban%20development%2C%20mountains%20in%20background%2C%20professional%20cityscape%20photography%20showing%20growing%20metropolitan%20area&width=280&height=360&seq=city-burnaby-001&orientation=portrait',
      link: '/burnaby-str-regulations'
    },
    {
      name: 'Surrey',
      note: 'Emerging rental market',
      image: 'https://readdy.ai/api/search-image?query=Surrey%20BC%20modern%20city%20center%20with%20contemporary%20buildings%20and%20urban%20development%2C%20clean%20streets%20and%20green%20spaces%2C%20growing%20metropolitan%20area%20photography%2C%20professional%20cityscape%20view&width=280&height=360&seq=city-surrey-001&orientation=portrait',
      link: '/surrey-rental-regulations'
    },
    {
      name: 'Langley',
      note: 'Flexible bylaw framework',
      image: 'https://readdy.ai/api/search-image?query=Langley%20BC%20suburban%20community%20with%20mix%20of%20residential%20homes%20and%20commercial%20areas%2C%20family-friendly%20neighborhood%20setting%2C%20clean%20modern%20development%20with%20green%20spaces%2C%20professional%20community%20photography&width=280&height=360&seq=city-langley-001&orientation=portrait',
      link: '/langley-str-licensing'
    },
    {
      name: 'Victoria',
      note: 'Tourism-driven capital city',
      image: 'https://readdy.ai/api/search-image?query=Victoria%20BC%20Inner%20Harbour%20waterfront%20with%20historic%20Parliament%20Buildings%20and%20boats%20in%20marina%2C%20beautiful%20coastal%20city%20with%20flowers%20and%20gardens%2C%20charming%20capital%20city%20atmosphere%2C%20professional%20cityscape%20photography&width=280&height=360&seq=city-victoria-001&orientation=portrait',
      link: '/victoria-str-regulations'
    },
    {
      name: 'Whistler',
      note: 'Resort municipality TA zones',
      image: 'https://readdy.ai/api/search-image?query=Whistler%20Village%20pedestrian%20street%20with%20snow%20capped%20mountains%2C%20luxury%20ski%20resort%20town%20with%20alpine%20architecture%2C%20cozy%20shops%20and%20restaurants%2C%20beautiful%20mountain%20scenery%2C%20professional%20travel%20photography&width=280&height=360&seq=city-whistler-001&orientation=portrait',
      link: '/whistler-str-regulations'
    },
    {
      name: 'Toronto',
      note: '180-night annual cap',
      image: 'https://readdy.ai/api/search-image?query=Toronto%20skyline%20with%20CN%20Tower%20and%20downtown%20financial%20district%2C%20modern%20skyscrapers%20and%20Lake%20Ontario%20waterfront%2C%20professional%20cityscape%20photography%20showing%20iconic%20Canadian%20metropolis&width=280&height=360&seq=city-toronto-001&orientation=portrait',
      link: '/toronto-str-regulations'
    },
    {
      name: 'Alberta Markets',
      note: 'Provincial regulation focus',
      image: 'https://readdy.ai/api/search-image?query=Alberta%20city%20skyline%20with%20modern%20downtown%20buildings%20and%20wide%20open%20prairie%20sky%2C%20clean%20urban%20development%20with%20mountain%20views%20in%20distance%2C%20professional%20cityscape%20photography%20showing%20Alberta%20architecture&width=280&height=360&seq=city-alberta-001&orientation=portrait',
      link: '/alberta-short-term-rental-regulations'
    }
  ];

  const handleCityClick = (city: typeof cities[0]) => {
    if (city.link) {
      window.REACT_APP_NAVIGATE(city.link);
    } else {
      openChatbot();
    }
  };

  return (
    <section id="cities" className="py-24 lg:py-32 bg-white overflow-hidden w-full">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Local knowledge matters
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Rules vary widely by city and even by building. We specialize in city-specific strategies including licensing requirements, enforcement patterns, and local demand dynamics.
          </p>
        </div>

        <div className="relative">
          {/* Left Arrow */}
          <button
            onClick={scrollLeft}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center cursor-pointer transition-all hover:bg-[#F0F0F0] hover:shadow-xl -ml-2 lg:-ml-6 ${
              canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            aria-label="Scroll left"
          >
            <i className="ri-arrow-left-s-line text-2xl text-[#505A5A]"></i>
          </button>

          {/* Right Arrow */}
          <button
            onClick={scrollRight}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center cursor-pointer transition-all hover:bg-[#F0F0F0] hover:shadow-xl -mr-2 lg:-mr-6 ${
              canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            aria-label="Scroll right"
          >
            <i className="ri-arrow-right-s-line text-2xl text-[#505A5A]"></i>
          </button>

          <div 
            ref={scrollContainerRef}
            className="overflow-x-auto pb-8 -mx-6 px-6 lg:mx-0 lg:px-0 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex gap-8 lg:justify-start" style={{ minWidth: 'min-content' }}>
              {cities.map((city, index) => (
                <div 
                  key={index}
                  className="flex-shrink-0 w-[280px] bg-[#F0F0F0] rounded-2xl overflow-hidden shadow-md hover:shadow-xl hover:scale-105 transition-all cursor-pointer"
                  onClick={() => handleCityClick(city)}
                >
                  <div className="relative h-[216px] overflow-hidden">
                    <img 
                      src={city.image}
                      alt={`${city.name} cityscape`}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-[#505A5A] mb-2">
                      {city.name}
                    </h3>
                    <p className="text-sm text-[#505A5A]/70">
                      {city.note}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="text-[#505A5A]/60 text-sm mb-4">Not sure about your city's rules?</p>
          <button 
            onClick={openChatbot}
            className="bg-[#882629] text-white px-8 py-4 rounded-full text-base font-medium hover:bg-[#6E1F22] transition-all cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl inline-flex items-center gap-3"
          >
            <i className="ri-chat-smile-2-line text-lg"></i>
            Ask our AI about your city
          </button>
        </div>
      </div>
    </section>
  );
}
